import clsx from 'clsx'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { createContext, useContext } from 'react'
import { cn } from '../../lib/utils'
import { themeClasses } from '../shared/themeClasses'

const tableSurfaceVariants = cva('surface-panel landing-surface-soft rounded-xl', {
  variants: {
    gutter: {
      default: '[--gutter:--spacing(4)] sm:[--gutter:--spacing(6)]',
      none: '[--gutter:0px]',
    },
  },
  defaultVariants: {
    gutter: 'default',
  },
})

interface TableContextValue {
  bleed: boolean
  dense: boolean
  grid: boolean
  striped: boolean
}

const TableContext = createContext<TableContextValue>({
  bleed: false,
  dense: false,
  grid: false,
  striped: false,
})

interface TableProps extends ComponentPropsWithoutRef<'div'> {
  bleed?: boolean
  dense?: boolean
  grid?: boolean
  striped?: boolean
  children?: ReactNode
  surface?: boolean
  gutter?: VariantProps<typeof tableSurfaceVariants>['gutter']
}

const Table = React.forwardRef<HTMLDivElement, TableProps>(function Table(
  {
    bleed = false,
    dense = false,
    grid = false,
    striped = false,
    surface = false,
    gutter,
    className,
    children,
    ...props
  },
  ref
) {
  return (
    <TableContext.Provider value={{ bleed, dense, grid, striped }}>
      <div
        ref={ref}
        className="flow-root ui-table-frame"
        data-slot="table-frame"
        data-table-dense={dense ? 'true' : 'false'}
        data-table-grid={grid ? 'true' : 'false'}
        data-table-striped={striped ? 'true' : 'false'}
      >
        <div
          {...props}
          data-slot="table-scroll"
          className={cn(
            surface && tableSurfaceVariants({ gutter }),
            className,
            'ui-table-scroll -mx-(--gutter) overflow-x-auto whitespace-nowrap'
          )}
        >
          <div className={cn('ui-table-inner inline-block min-w-full align-middle', !bleed && 'sm:px-(--gutter)')}>
            <table data-slot="table" className={cn('ui-table min-w-full caption-bottom text-left text-sm/6', themeClasses.textPrimary)}>
              {children}
            </table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  )
})

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(function TableHeader(
  { className, ...props },
  ref
) {
  return <thead ref={ref} data-slot="table-head" className={cn(className, 'ui-table-head', themeClasses.textMuted)} {...props} />
})

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(function TableBody(
  { className, ...props },
  ref
) {
  return <tbody ref={ref} data-slot="table-body" className={cn(className, 'ui-table-body')} {...props} />
})

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(function TableFooter(
  { className, ...props },
  ref
) {
  return (
    <tfoot
      ref={ref}
      className={cn('border-t border-zinc-200 bg-zinc-50/50 font-medium dark:border-zinc-800 dark:bg-zinc-900/50 [&>tr]:last:border-b-0', className)}
      {...props}
    />
  )
})

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(function TableRow(
  { className, ...props },
  ref
) {
  const { striped } = useContext(TableContext)

  return (
    <tr
      ref={ref}
      data-slot="table-row"
      className={cn(
        className,
        'ui-table-row',
        striped && themeClasses.tableStripe,
        striped && themeClasses.tableHoverStriped,
        !striped && themeClasses.tableHoverDefault
      )}
      {...props}
    />
  )
})

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(function TableHead(
  { className, scope, ...props },
  ref
) {
  const { bleed, grid } = useContext(TableContext)

  return (
    <th
      ref={ref}
      scope={scope ?? 'col'}
      data-slot="table-header"
      className={cn(
        className,
        'ui-table-header h-auto border-b px-4 py-2 text-left align-middle font-medium first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2))',
        themeClasses.dividerBorder,
        grid && clsx('border-l first:border-l-0', themeClasses.dividerBorderSoft),
        !bleed && 'sm:first:pl-1 sm:last:pr-1',
        themeClasses.textMuted
      )}
      {...props}
    />
  )
})

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(function TableCell(
  { className, ...props },
  ref
) {
  const { bleed, dense, grid, striped } = useContext(TableContext)

  return (
    <td
      ref={ref}
      data-slot="table-cell"
      className={cn(
        className,
        'ui-table-cell relative align-middle px-4 first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2))',
        !striped && clsx('border-b', themeClasses.dividerBorderSoft),
        grid && clsx('border-l first:border-l-0', themeClasses.dividerBorderSoft),
        dense ? 'py-2.5' : 'py-4',
        !bleed && 'sm:first:pl-1 sm:last:pr-1'
      )}
      {...props}
    />
  )
})

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(function TableCaption(
  { className, ...props },
  ref
) {
  return <caption ref={ref} className={cn('mt-4 text-sm text-zinc-500 dark:text-zinc-400', className)} {...props} />
})

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
}
